/**
 * Commands a client may send in an `act` message (docs/design/02 §4.2, §5.2; owner decision Q48: no
 * emotes). The seat is never part of a command: the server takes it from the authenticated socket.
 *
 * Limits mirror the engine's `LIMITS`; the engine re-checks every one of them.
 */
import { LIMITS, MARKER_NAME_PATTERN, MAX_CHANGE, MAX_TARGETS, UNDO_DEPTH } from '@play/engine';
import { z } from 'zod';
import { AnswerSchema } from './decisions';
import {
  BattlefieldIdSchema,
  ChainItemIdSchema,
  DecisionIdSchema,
  DeckPositionSchema,
  DeckZoneKindSchema,
  ObjectIdSchema,
  PhaseSchema,
  PlayerIdSchema,
  TokenIdSchema,
  ZoneRefSchema,
  int,
  text,
} from './primitives';

const objects = z.array(ObjectIdSchema).min(1).max(LIMITS.objectsPerCommand);
const cards = int(1, LIMITS.cardsPerCommand);
const marker = z.string().regex(MARKER_NAME_PATTERN);
const nonZero = (max: number) => int(-max, max).refine((n) => n !== 0, 'delta must not be 0');

/** A change a chain item makes to one of its targets by hand (`modify`). */
export const SpellChangeSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('damage'), n: nonZero(MAX_CHANGE) }),
  z.strictObject({ kind: z.literal('might'), n: nonZero(MAX_CHANGE) }),
  z.strictObject({ kind: z.literal('buff'), n: nonZero(MAX_CHANGE) }),
  z.strictObject({ kind: z.literal('stun'), on: z.boolean() }),
  z.strictObject({ kind: z.literal('exhaust') }),
  z.strictObject({ kind: z.literal('ready') }),
  z.strictObject({ kind: z.literal('kill') }),
  z.strictObject({ kind: z.literal('banish') }),
  z.strictObject({ kind: z.literal('returnToHand') }),
  z.strictObject({ kind: z.literal('move'), to: z.union([z.literal('base'), BattlefieldIdSchema]) }),
  z.strictObject({ kind: z.literal('counter') }),
]);

/** Every tier-1 table command (and `decide`, used by every tier). */
export const TableCommandSchema = z.discriminatedUnion('t', [
  z.strictObject({
    t: z.literal('move'),
    object: ObjectIdSchema,
    to: ZoneRefSchema,
    position: DeckPositionSchema.exactOptional(),
    exhausted: z.boolean().exactOptional(),
  }),
  z.strictObject({
    t: z.literal('moveTop'),
    zone: DeckZoneKindSchema,
    n: cards,
    to: ZoneRefSchema,
    position: DeckPositionSchema.exactOptional(),
    exhausted: z.boolean().exactOptional(),
  }),
  z.strictObject({ t: z.literal('draw'), n: cards }),
  z.strictObject({ t: z.literal('channel'), n: cards }),
  z.strictObject({ t: z.literal('shuffle'), zone: DeckZoneKindSchema }),
  z.strictObject({ t: z.literal('lookAtTop'), zone: DeckZoneKindSchema, n: cards }),
  z.strictObject({ t: z.literal('reveal'), objects, to: z.array(PlayerIdSchema).min(1).max(8).exactOptional() }),
  z.strictObject({ t: z.literal('exhaust'), objects }),
  z.strictObject({ t: z.literal('ready'), objects }),
  z.strictObject({ t: z.literal('readyAll') }),
  z.strictObject({
    t: z.literal('counter'),
    object: ObjectIdSchema,
    kind: marker,
    delta: nonZero(LIMITS.counterDelta),
  }),
  z.strictObject({ t: z.literal('status'), object: ObjectIdSchema, status: marker, on: z.boolean() }),
  z.strictObject({ t: z.literal('attach'), object: ObjectIdSchema, to: ObjectIdSchema }),
  z.strictObject({ t: z.literal('detach'), object: ObjectIdSchema }),
  z.strictObject({
    t: z.literal('createToken'),
    tokenId: TokenIdSchema,
    to: ZoneRefSchema,
    n: int(1, LIMITS.tokensPerCommand).exactOptional(),
    exhausted: z.boolean().exactOptional(),
  }),
  z.strictObject({ t: z.literal('points'), delta: nonZero(LIMITS.pointsDelta) }),
  z.strictObject({ t: z.literal('xp'), delta: nonZero(LIMITS.xpDelta) }),
  z.strictObject({
    t: z.literal('battlefieldControl'),
    battlefield: BattlefieldIdSchema,
    controller: PlayerIdSchema.nullable(),
  }),
  z.strictObject({ t: z.literal('giveControl'), object: ObjectIdSchema, to: PlayerIdSchema }),
  z.strictObject({
    t: z.literal('stack'),
    object: ObjectIdSchema,
    ability: z.enum(['activated', 'triggered']).exactOptional(),
  }),
  z.strictObject({
    t: z.literal('unstack'),
    item: ChainItemIdSchema,
    to: ZoneRefSchema.exactOptional(),
    position: DeckPositionSchema.exactOptional(),
    exhausted: z.boolean().exactOptional(),
  }),
  z.strictObject({ t: z.literal('phase'), to: PhaseSchema }),
  z.strictObject({ t: z.literal('endTurn') }),
  z.strictObject({ t: z.literal('passPriority') }),
  z.strictObject({ t: z.literal('pointAt'), from: ObjectIdSchema, to: z.union([ObjectIdSchema, ZoneRefSchema]) }),
  z.strictObject({
    t: z.literal('target'),
    item: ChainItemIdSchema,
    targets: z.array(ObjectIdSchema).max(MAX_TARGETS),
  }),
  z.strictObject({
    t: z.literal('modify'),
    item: ChainItemIdSchema,
    target: ObjectIdSchema,
    change: SpellChangeSchema,
  }),
  z.strictObject({ t: z.literal('undoRequest'), steps: int(1, UNDO_DEPTH).exactOptional() }),
  z.strictObject({ t: z.literal('undoAnswer'), accept: z.boolean() }),
  z.strictObject({ t: z.literal('concede') }),
  z.strictObject({ t: z.literal('decide'), decisionId: DecisionIdSchema, answer: AnswerSchema }),
]);

/**
 * Tier-2 advisor commands (docs/design/02 §5.2). Stub contract: accepted by the protocol, answered with
 * `notImplemented` until the rules layer lands.
 */
export const AdvisorCommandSchema = z.discriminatedUnion('t', [
  z.strictObject({ t: z.literal('recommended'), actionId: text(64), answers: z.array(AnswerSchema).max(16) }),
  z.strictObject({ t: z.literal('resolveCard'), object: ObjectIdSchema, answers: z.array(AnswerSchema).max(16) }),
  z.strictObject({ t: z.literal('dismissReminder'), reminderId: text(64) }),
  z.strictObject({ t: z.literal('applyEffect'), item: ChainItemIdSchema, answers: z.array(AnswerSchema).max(16) }),
]);

/** Any command (the server checks the tier). */
export const CommandSchema = z.union([TableCommandSchema, AdvisorCommandSchema]);
